import { GlobalStyles } from "@mui/material";
import { useSignal } from "@preact/signals-react";
import { useEffect } from "react";
import { Dialog, Snackbar } from "../../resolvers-types";
import { renderModalPopups } from "../components/ComponentUtils.tsx";
import NGDialog from "../components/NGDialog/NGDialog";
import NGSnackbar from "../components/NGSnackbar/NGSnackbar.tsx";
import NGLayout from "../layouts/NGLayout";
import { publishUserAction, UserAction } from "../library/analytics.ts";
import { useOidc } from "../library/auth.ts";
import { getState, setupHandlers, setupInitialState, setupLocalState, updateItemContext } from "../library/dataService";
import { INGPageProps } from "../library/NGFieldExtensions";

export default function NGPage({ config, layout, context }: INGPageProps) {
  const tag = "NGPage";
  //generate a function to return all the Applications (recursively) and containers (top level only) for a given page
  const dialog: Dialog = { Id: "NGMessage", __typename: "Dialog" };
  const snackbar: Snackbar = { Id: "NGSnackbarMessage", __typename: "Snackbar" };

  context.InDesignMode = config.InDesignMode as boolean;

  const currentContext = updateItemContext(context, config);

  const local = setupLocalState(
    config,
    {
      InDesignMode: useSignal(config.InDesignMode || false),
      Visible: useSignal(config.Visible ?? true),
      Theme: useSignal(config.Theme ?? "default"),
      Styles: useSignal(config.Styles ?? []),
    },
    currentContext
  );

  setupInitialState(config, currentContext);

  const handlers = setupHandlers(config, currentContext);

  const auth = useOidc();

  function makeIdsUnique(obj: any, seenIds = new Set<string>(), inActions = false): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => makeIdsUnique(item, seenIds, inActions));
    }

    if (obj && typeof obj === "object") {
      const newObj: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        if (key === "Id" && !inActions) {
          let newId = value;
          while (seenIds.has(newId)) {
            newId = `${value}_${Math.random().toString(36).substring(2, 8)}`;
          }
          seenIds.add(newId);
          newObj[key] = newId;
        } else if (key === "Actions") {
          newObj[key] = value.map((item: any) => makeIdsUnique(item, seenIds, true));
        } else {
          newObj[key] = makeIdsUnique(value, seenIds, inActions);
        }
      }
      return newObj;
    }

    return obj;
  }

  useEffect(() => {
    const { state } = getState(context);
    const isEditorPage = state.Page?.value.Id?.includes("-editor");
    const editorItemName = isEditorPage ? state.Page?.value.Items?.[0]?.Name : "";
    const basePageName = config.Label ?? config.Name ?? "Page";
    const documentTitle = editorItemName ? `${basePageName} - ${editorItemName}` : basePageName;
    document.title = documentTitle;

    let action: UserAction | null | undefined;

    if (auth.isNewBrowserSession) {
      action = publishUserAction(config, currentContext, "login", null, {
        EventCategory: "auth",
      });
    }
    action = publishUserAction(config, currentContext, "page_view", null, {
      EventCategory: "navigation",
      IsImplicit: true,
    });
    if (handlers["onLoad"]) {
      handlers["onLoad"](config, config, action);
    }

    const c2 = makeIdsUnique(config, new Set<string>());

    console.log(`ToSave`, c2);
  }, [config]);

  return (
    <>
      <GlobalStyles styles={{ ...local.Styles.value }} />
      <NGDialog key="messageDialog" config={dialog} context={currentContext} />
      <NGSnackbar key="snackbar" config={snackbar} context={currentContext} />
      {renderModalPopups(config, currentContext)}
      {local.Visible.value && layout && (
        <NGLayout
          layout={layout}
          layoutItem={config}
          inDesignMode={local.InDesignMode.value}
          context={currentContext}
        />
      )}
    </>
  );
}
